import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  TIMELINE_AGGREGATIONS, TIMELINE_EMPTY_BUCKETS, TIMELINE_GRANULARITIES,
  effectiveTimelineRecipe, validateTimelineOverride,
  type TimelineAggregation, type TimelineEmptyBuckets, type TimelineGranularity,
  type TimelineOverride, type TimelineRecipe, type TimelineSeriesSpec,
} from '@solum/experience'
import {
  Button, CustomizeRowMenu, Field, Input, Modal, Select, TimelineChart,
  type TimelineChartData,
} from '@solum/ui'
import { useSolum } from '../data/client'
import { useTimelinePreferences } from './useTimelinePreferences'
import s from './RelationTimeline.module.css'

interface TimelineField {
  column_name: string
  label: string
  role: 'time' | 'value'
  storage_type: string
}

interface TimelineResponse extends TimelineChartData {
  entity: string
  id: string
  slot: string
  target: string
  recipe: TimelineRecipe
  from: string
  to: string
  status: 'ok' | 'empty'
}

const options = (values: readonly string[]) => values.map((value) => ({
  value, label: value.charAt(0).toUpperCase() + value.slice(1),
}))

const viewerZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' } catch { return 'UTC' }
}

const refused = (error: unknown) => /permission|refus|not allowed|not available/iu.test(String(error))

export interface RelationTimelineProps {
  entity: string
  recordId: string
  slot: string
  target: string
  recipe: TimelineRecipe
}

/**
 * The console binding for the shared, data-free Timeline chart. The server
 * resolves the recipe and aggregates under caller RLS; this host contributes
 * only the current record, viewer zone and the person's finite override.
 */
export function RelationTimeline({ entity, recordId, slot, target, recipe }: RelationTimelineProps) {
  const { client } = useSolum()
  const preferences = useTimelinePreferences(entity)
  const stored = preferences.overrideFor(slot)
  const effective = useMemo(() => effectiveTimelineRecipe(recipe, stored), [recipe, stored])
  const [editing, setEditing] = useState(false)

  const series = useQuery({
    queryKey: ['timeline-series', entity, recordId, slot, effective.recipe, viewerZone()],
    // effectiveTimelineRecipe already falls back to the governed recipe when
    // a stored override is stale. Do not let that stale preference suppress
    // the governed Timeline itself.
    enabled: !!entity && !!recordId && !!slot,
    queryFn: () => client.raw().rpcScalar<TimelineResponse>('timeline_series', {
      p_entity: entity,
      p_id: recordId,
      p_slot: slot,
      p_recipe: effective.recipe,
      p_time_zone: effective.recipe.timeZone === 'viewer' ? viewerZone() : 'UTC',
    }, 'core'),
    staleTime: 30_000,
  })

  const state = series.isLoading ? 'loading'
    : series.isError ? (refused(series.error) ? 'refused' : 'error')
      : series.data?.status === 'empty' ? 'empty' : 'ready'

  return (
    <>
      <TimelineChart
        data={series.data}
        state={state}
        problem={series.error ? String(series.error) : null}
        actions={(
          <CustomizeRowMenu label="Timeline" actions={[{
            key: 'customize-timeline', label: 'Customize timeline', onSelect: () => setEditing(true),
          }]} />
        )}
        testId={`relationship-timeline-${slot}`}
      />
      {editing && (
        <TimelineSettings
          target={target}
          governed={recipe}
          initial={stored}
          onSave={(next) => { preferences.setOverride(slot, next); setEditing(false) }}
          onReset={() => { preferences.resetOverride(slot); setEditing(false) }}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  )
}

function TimelineSettings({ target, governed, initial, onSave, onReset, onClose }: {
  target: string
  governed: TimelineRecipe
  initial?: TimelineOverride
  onSave: (value: TimelineOverride) => void
  onReset: () => void
  onClose: () => void
}) {
  const { client } = useSolum()
  const seed = effectiveTimelineRecipe(governed, initial).recipe
  const [timeField, setTimeField] = useState(seed.timeField)
  const [granularity, setGranularity] = useState<TimelineGranularity>(seed.granularity)
  const [timeZone, setTimeZone] = useState(seed.timeZone)
  const [rangeValue, setRangeValue] = useState(String(seed.range.value))
  const [rangeUnit, setRangeUnit] = useState<TimelineGranularity>(seed.range.unit)
  const [emptyBuckets, setEmptyBuckets] = useState<TimelineEmptyBuckets>(seed.emptyBuckets)
  const [chartSeries, setChartSeries] = useState<TimelineSeriesSpec[]>(seed.series)
  const [problem, setProblem] = useState<string | null>(null)

  const fields = useQuery({
    queryKey: ['timeline-fields', target],
    queryFn: () => client.raw().rpcTable<TimelineField>('timeline_fields', { p_entity: target }, 'core'),
    staleTime: 60_000,
  })
  const timeFields = useMemo(
    () => fields.data?.filter((field) => field.role === 'time') ?? [], [fields.data])
  const valueFields = useMemo(
    () => fields.data?.filter((field) => field.role === 'value') ?? [], [fields.data])
  const fieldsProblem = fields.isError ? String(fields.error) : null

  useEffect(() => {
    if (!timeFields.length || timeFields.some((field) => field.column_name === timeField)) return
    setTimeField(timeFields[0]!.column_name)
  }, [timeField, timeFields])

  const updateSeries = (index: number, next: TimelineSeriesSpec) => {
    setChartSeries((current) => current.map((entry, at) => at === index ? next : entry))
  }

  const commit = () => {
    const parsedRange = Number(rangeValue)
    const next: TimelineOverride = {
      timeField,
      granularity,
      timeZone,
      range: { value: parsedRange, unit: rangeUnit },
      series: chartSeries,
      emptyBuckets,
    }
    const issues = validateTimelineOverride(next)
    const merged = effectiveTimelineRecipe(governed, next)
    const all = [...issues, ...merged.issues]
    if (all.length > 0) { setProblem(all.join('. ')); return }
    onSave(next)
  }

  return (
    <Modal label="Customize timeline" onClose={onClose} width={680} testid="timeline-settings">
      <div className={s.dialog}>
        <header><h2>Customize timeline</h2><p>These choices apply only to you and this relationship.</p></header>
        {fieldsProblem && <p className={s.problem} role="alert">Timeline fields could not be loaded: {fieldsProblem}</p>}
        <div className={s.fields}>
          <Field label="Date axis">
            <Select value={timeField} onValueChange={setTimeField}
              options={timeFields.map((field) => ({ value: field.column_name, label: field.label }))}
              placeholder={fields.isLoading ? 'Loading fields...' : 'Choose a date field'} />
          </Field>
          <Field label="Granularity">
            <Select value={granularity} onValueChange={(value) => setGranularity(value as TimelineGranularity)}
              options={options(TIMELINE_GRANULARITIES)} />
          </Field>
          <Field label="Time zone">
            <Select value={timeZone} onValueChange={(value) => setTimeZone(value as 'viewer' | 'utc')}
              options={[{ value: 'viewer', label: `My time zone (${viewerZone()})` }, { value: 'utc', label: 'UTC' }]} />
          </Field>
          <Field label="Empty buckets">
            <Select value={emptyBuckets}
              onValueChange={(value) => setEmptyBuckets(value as TimelineEmptyBuckets)}
              options={options(TIMELINE_EMPTY_BUCKETS)} />
          </Field>
          <Field label="Range">
            <div className={s.range}>
              <Input type="number" min={1} step={1} value={rangeValue}
                onChange={(event) => setRangeValue(event.target.value)} aria-label="Range value" />
              <Select value={rangeUnit} onValueChange={(value) => setRangeUnit(value as TimelineGranularity)}
                options={options(TIMELINE_GRANULARITIES)} aria-label="Range unit" />
            </div>
          </Field>
        </div>

        <section className={s.series} aria-labelledby="timeline-series-heading">
          <div className={s.seriesHead}>
            <div><h3 id="timeline-series-heading">Series</h3><p>Up to four readings can share the chart.</p></div>
            <Button size="sm" disabled={chartSeries.length >= 4}
              onClick={() => setChartSeries((current) => [...current, { aggregation: 'count' }])}>Add series</Button>
          </div>
          {chartSeries.map((entry, index) => (
            <div className={s.seriesRow} key={`${index}-${entry.aggregation}-${entry.field ?? ''}`}>
              <Select value={entry.aggregation}
                onValueChange={(value) => updateSeries(index, value === 'count'
                  ? { aggregation: 'count' }
                  : { aggregation: value as TimelineAggregation, field: entry.field ?? valueFields[0]?.column_name })}
                options={options(TIMELINE_AGGREGATIONS)} aria-label={`Aggregation ${index + 1}`} />
              <Select value={entry.field ?? ''} disabled={entry.aggregation === 'count'}
                onValueChange={(field) => updateSeries(index, { ...entry, field })}
                options={valueFields.map((field) => ({ value: field.column_name, label: field.label }))}
                placeholder={entry.aggregation === 'count' ? 'Counts rows' : 'Choose a numeric field'}
                aria-label={`Field ${index + 1}`} />
              <Button size="sm" variant="ghost" disabled={chartSeries.length === 1}
                onClick={() => setChartSeries((current) => current.filter((_, at) => at !== index))}>Remove</Button>
            </div>
          ))}
        </section>

        {problem && <p className={s.problem} role="alert">{problem}</p>}
        <footer className={s.footer}>
          <Button variant="ghost" onClick={onReset}>Reset to governed default</Button>
          <span className={s.grow} />
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={commit}>Save timeline</Button>
        </footer>
      </div>
    </Modal>
  )
}
